import { saveLevelLocally } from "../helpers/saveLevelLocally";
import { getSelectedPlacement } from "../atoms/selectedPlacementAtom";
import { PLACEMENT_TYPE_HERO } from "../helpers/consts";
import placementFactory from "./PlacementFactory";

export class LevelEditor {
  constructor(level) {
    this.level = level;
  }

  handlePlacementClick(x, y, selectedId) {
    // Don't allow clicking on the Hero
    if (x === this.level.heroRef.x && y === this.level.heroRef.y) {
      return;
    }

    const candidate = this.buildPlacementCandidate(x, y, selectedId);

    // Remove placements on the same layer as placement candidate
    const placementAtPosition = this.findPlacementAtPosition(candidate);
    if (placementAtPosition) {
      this.removePlacementsOnLayerPosition(x, y, candidate.getEditingLayer());
      return;
    }

    // Edit placements that can only have one (like Hero, Goal)
    if (candidate.isOnlyType()) {
      this.updatePlacementOfSameType(x, y, candidate.type);
      return;
    }

    // Tile is clear, so add a new placement
    this.addPlacement(candidate);
  }

  buildPlacementCandidate(x, y, selectedId) {
    const placementSelection = getSelectedPlacement(selectedId);
    return placementFactory.createPlacement(
      {
        type: placementSelection.type,
        x,
        y,
        ...(placementSelection.initialState ?? {}),
      },
      this.level
    );
  }

  findPlacementAtPosition(placementCandidate) {
    return this.level.placements.find((p) => {
      return (
        p.x === placementCandidate.x &&
        p.y === placementCandidate.y &&
        p.getEditingLayer() === placementCandidate.getEditingLayer()
      );
    });
  }

  addPlacement(candidate) {
    //New for this layer, so create a new placement
    //Shoot down if out of bounds
    const isInBounds =
      candidate.x > 0 &&
      candidate.y > 0 &&
      candidate.x <= this.level.tilesWidth &&
      candidate.y <= this.level.tilesHeight;
    if (!isInBounds) {
      return;
    }
    this.level.placements = [...this.level.placements, candidate];
    this.saveLocally();
  }

  updatePlacementOfSameType(x, y, type) {
    const placementToUpdate = this.level.placements.find(
      (p) => p.type === type
    );
    this.level.updateLivePlacement(placementToUpdate, {
      x: x,
      y: y,
    });
    this.saveLocally();
  }

  removePlacementsOnLayerPosition(x, y, layer) {
    this.level.placements = this.level.placements.filter((p) => {
      if (p.isOnlyType()) {
        return true;
      }
      const isMatch = p.x === x && p.y === y && p.getEditingLayer() === layer;
      return !isMatch;
    });
    this.saveLocally();
  }

  clearPlacements() {
    this.level.placements = this.level.placements.filter((p) => {
      return p.type === PLACEMENT_TYPE_HERO || p.isOnlyType();
    });
  }

  updateTitle(newTitle) {
    this.level.title = newTitle;
    this.saveLocally();
  }

  updateTheme(newTheme) {
    this.level.theme = newTheme;
    this.saveLocally();
  }

  updateTilesWidth(change) {
    this.level.tilesWidth = this.level.tilesWidth + change;
    this.saveLocally();
  }

  updateTilesHeight(change) {
    this.level.tilesHeight = this.level.tilesHeight + change;
    this.saveLocally();
  }

  //Remember, this local storage stuff might not make the tutorial.
  saveLocally() {
    if (this.level.isEditableByUser) {
      saveLevelLocally(this.level.id, this.getJSON());
    }
  }

  getJSON() {
    return {
      title: this.level.title,
      isEditableByUser: this.level.isEditableByUser,
      theme: this.level.theme,
      tilesWidth: this.level.tilesWidth,
      tilesHeight: this.level.tilesHeight,
      placements: this.level.placements
        .filter((p) => {
          // Only save placements that Placed in editor (not live bodies)
          return !p.wasSpawnedAfterMapStart;
        })
        .map((p) => {
          return p.toJSON();
        }),
    };
  }
}
